import analytics from './analytics'

type StorageType = 'local' | 'session'

function getStorage(storageType: StorageType): Storage {
  return storageType === 'local' ? localStorage : sessionStorage
}

function logStorageWarning({
  operation,
  storageType,
  key,
  error,
}: {
  operation: string
  storageType: StorageType
  key: string
  error: unknown
}) {
  // sample query: index=cheetah name="storage-helpers"
  analytics.logWarning({
    name: 'storage-helpers',
    message: `${operation} failed for "${key}" in ${storageType}Storage: ${
      error instanceof Error ? error.message : String(error)
    }`,
  })
}

export function getFromStorage({
  storageType,
  key,
}: {
  storageType: StorageType
  key: string
}): string | null {
  if (globalThis.window === undefined) return null
  try {
    return getStorage(storageType)?.getItem(key) ?? null
  } catch (error) {
    // storage can throw rather than just being null (e.g. Safari private mode, restricted iframes, bots)
    logStorageWarning({ operation: 'getItem', storageType, key, error })
    return null
  }
}

export function setToStorage({
  storageType,
  key,
  value,
}: {
  storageType: StorageType
  key: string
  value: string
}): void {
  if (globalThis.window === undefined) return
  try {
    getStorage(storageType)?.setItem(key, value)
  } catch (error) {
    // storage unavailable — no-op, but log so a real failure doesn't disappear silently
    logStorageWarning({ operation: 'setItem', storageType, key, error })
  }
}

export function removeFromStorage({ storageType, key }: { storageType: StorageType; key: string }): void {
  if (globalThis.window === undefined) return
  try {
    getStorage(storageType)?.removeItem(key)
  } catch (error) {
    // storage unavailable — no-op, but log so a real failure doesn't disappear silently
    logStorageWarning({ operation: 'removeItem', storageType, key, error })
  }
}
